import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Grid } from '@mui/material';
import {
  Person,
  Business,
  Security,
  Notifications,
  Receipt,
  Group,
  CreditCard,
  Subscriptions,
} from '@mui/icons-material';
import PaperSection from '../../components/PaperSection';
import SettingsMenuItem from '../../components/SettingsMenuItem';
import ContentWrapper from '../../layout/ContentWrapper';
import { UserRole } from '@avanzu/types';
import { useAuth } from '../../contexts/AuthContext';
import { ProfileRoles } from '../settings/Profile';
import { TeamRoles } from '../settings/Team';
import { OrganisationRoles } from '../settings/Organisation';
import { NotificationRoles } from '../settings/Notifications';
import { InvoiceRoles } from '../settings/Invoices';
import { XeroRoles } from '../settings/Xero';
import { PlanRoles } from '../settings/Plan';
import { SecurityRoles } from '../settings/Security';

interface SettingItem {
  title: string;
  description: string;
  icon: React.ReactNode;
  path: string;
  roles: UserRole[];
}

export const Settings: React.FC = () => {
  const navigate = useNavigate();
  const { currentOrganisation } = useAuth();

  // note the order is left to right, top to bottom
  const settingItems: SettingItem[] = [
    {
      title: 'Profile',
      description: 'Manage your personal information and preferences',
      icon: <Person />,
      path: '/settings/profile',
      roles: ProfileRoles,
    },
    {
      title: 'Team Members',
      description: 'Manage your team members and their roles',
      icon: <Group />,
      path: '/settings/team',
      roles: TeamRoles,
    },
    {
      title: 'Organisation Details',
      description: 'View and manage your organization details',
      icon: <Business />,
      path: '/settings/organisation',
      roles: OrganisationRoles,
    },
    {
      title: 'Notifications',
      description: 'Control email and in-app notification preferences',
      icon: <Notifications />,
      path: '/settings/notifications',
      roles: NotificationRoles,
    },
    {
      title: 'Billing & Invoices',
      description: 'View and manage your credit card details and invoices',
      icon: <CreditCard />,
      path: '/settings/billing',
      roles: InvoiceRoles,
    },

    {
      title: 'Xero Integration',
      description: 'Connect and manage your Xero account settings',
      icon: <Receipt />,
      path: '/settings/xero',
      roles: XeroRoles,
    },
    {
      title: 'Purchase Project',
      description: 'Change or upgrade your project',
      icon: <Subscriptions />,
      path: '/settings/plan',
      roles: PlanRoles,
    },
    {
      title: 'Security and Privacy',
      description: 'Update password and manage security settings',
      icon: <Security />,
      path: '/settings/security',
      roles: SecurityRoles,
    },
  ];

  const handleNavigate = (path: string) => {
    navigate(path);
  };

  return (
    <>
      <ContentWrapper>
        <PaperSection title="Settings">
          <Grid container spacing={2}>
            {settingItems.map(
              (item) =>
                item.roles.includes(currentOrganisation?.role || UserRole.VIEWER) && (
                  <Grid item xs={12} sm={6} key={item.title}>
                    <SettingsMenuItem
                      title={item.title}
                      description={item.description}
                      icon={item.icon}
                      onClick={() => handleNavigate(item.path)}
                    />
                  </Grid>
                )
            )}
          </Grid>
        </PaperSection>
      </ContentWrapper>
    </>
  );
};

export default Settings;
